import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, take } from 'rxjs';
import { ChatMessage, MessageStatus } from '../models/chat-message';
import { User } from '../models/user';
import { Document } from '../models/document';
import { AutocompleteItem } from '../models/autocomplete-item';

@Injectable({
  providedIn: 'root',
})
export class ChatService {
  private readonly apiUrl = '/api/Chat/';

  private messagesSubject = new BehaviorSubject<ChatMessage[]>([]);
  public messages$ = this.messagesSubject.asObservable();

  private projectUsersSubject = new BehaviorSubject<User[]>([]);
  public projectUsers$ = this.projectUsersSubject.asObservable();

  private projectDocumentsSubject = new BehaviorSubject<Document[]>([]);
  public projectDocuments$ = this.projectDocumentsSubject.asObservable();

  private activeDocumentIdSubject = new BehaviorSubject<string | null>(null);
  public activeDocumentId$ = this.activeDocumentIdSubject.asObservable();

  constructor(private httpClient: HttpClient) {}

  /**
   * Получение сообщений для конкретного проекта
   */
  getMessages(projectId: string): void {
    this.httpClient
      .get<ChatMessage[]>(`${this.apiUrl}${projectId}/messages`)
      .pipe(take(1))
      .subscribe({
        next: (messages) => this.messagesSubject.next(messages),
        error: (err) => console.error('Ошибка загрузки сообщений', err),
      });
  }

  /**
   * Отправка нового сообщения
   */
  sendMessage(message: ChatMessage): Observable<ChatMessage> {
    return this.httpClient.post<ChatMessage>(`${this.apiUrl}send`, message);
  }

  /**
   * Локальное добавление сообщения (для обновлений в реальном времени через SignalR)
   */
  addMessageLocal(message: ChatMessage): void {
    const messages = this.messagesSubject.value;
    this.messagesSubject.next([...messages, message]);
  }

  /**
   * Локальное обновление статуса сообщения
   */
  updateMessageStatus(messageId: string, status: MessageStatus): void {
    const messages = this.messagesSubject.value;
    const updatedMessages = messages.map((msg) =>
      msg.id === messageId ? { ...msg, status } : msg
    );
    this.messagesSubject.next(updatedMessages);
  }

  /**
   * Получение пользователей для конкретного проекта
   */
  getProjectUsers(projectId: string): void {
    this.httpClient
      .get<User[]>(`${this.apiUrl}${projectId}/users`)
      .pipe(take(1))
      .subscribe({
        next: (users) => this.projectUsersSubject.next(users),
        error: (err) => console.error('Ошибка загрузки пользователей проекта', err),
      });
  }

  /**
   * Получение документов для конкретного проекта
   */
  getProjectDocuments(projectId: string): void {
    this.httpClient
      .get<Document[]>(`${this.apiUrl}${projectId}/documents`)
      .pipe(take(1))
      .subscribe({
        next: (documents) => this.projectDocumentsSubject.next(documents),
        error: (err) => console.error('Ошибка загрузки документов проекта', err),
      });
  }

  /**
   * Установка ID активного документа
   */
  setActiveDocumentId(documentId: string | null): void {
    this.activeDocumentIdSubject.next(documentId);
  }

  /**
   * Фильтрация пользователей для автодополнения
   */
  filterUsers(query: string): AutocompleteItem[] {
    const users = this.projectUsersSubject.value;
    const lowerQuery = query.toLowerCase();

    return users
      .filter(
        (user) =>
          user.displayName?.toLowerCase().includes(lowerQuery) ||
          user.userName?.toLowerCase().includes(lowerQuery)
      )
      .map((user) => ({
        id: user.id!,
        displayText: user.displayName || user.userName || '',
        secondaryText: user.userName || undefined,
        type: 'user' as const,
      }));
  }

  /**
   * Фильтрация документов для автодополнения
   */
  filterDocuments(query: string): AutocompleteItem[] {
    const documents = this.projectDocumentsSubject.value;
    const activeDocumentId = this.activeDocumentIdSubject.value;
    const lowerQuery = query.toLowerCase();

    let filteredDocuments = documents.filter((doc) =>
      doc.name.toLowerCase().includes(lowerQuery)
    );

    // Перемещение активного документа на первое место
    if (activeDocumentId) {
      filteredDocuments = filteredDocuments.sort((a, b) => {
        if (a.id === activeDocumentId) return -1;
        if (b.id === activeDocumentId) return 1;
        return 0;
      });
    }

    return filteredDocuments.map((doc) => ({
      id: doc.id,
      displayText: doc.name,
      secondaryText: doc.extension,
      type: 'document' as const,
    }));
  }

  /**
   * Сброс состояния сервиса
   */
  reset(): void {
    this.messagesSubject.next([]);
    this.projectUsersSubject.next([]);
    this.projectDocumentsSubject.next([]);
    this.activeDocumentIdSubject.next(null);
  }
}
